import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ContentLayout2 } from '@components/content/components/content-layout-2/model';
import { HeroLayout2 } from '@components/hero/components/hero-layout-2/model';
import { StoreNameService } from '@components/hero/components/hero-layout-2/services';
import { ContextService } from '@services/context';
import { ElasticSearchService } from '@services/elastic-search';
import { LogsService } from '@services/logs';
import { MiddleOfficeService } from '@services/middle-office';
import { ResponsiveService } from '@services/responsive';
import { ReviewsService } from '@services/reviews';
import { AverageGrade } from '@services/reviews/models';
import { SeoService } from '@services/seo';
import { JsonLD } from '@services/seo/model';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { getCDNLink } from '@shared/components/image/utils';
import { RateComponent } from '@shared/components/rate';
import { ScheduleComponent } from '@shared/components/schedule';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { catchError, combineLatest, filter, of, Subject, switchMap, takeUntil } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PageStore } from 'src/app/stores';

const parisDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' });

@Component({
  selector: 'app-content-layout-2',
  imports: [TitleComponent, ButtonComponent, ScheduleComponent, RouterLink, AsyncPipe, RateComponent, ImageComponent],
  templateUrl: './content-layout-2.component.html',
  styleUrl: './content-layout-2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentLayout2Component implements OnInit, OnDestroy {
  readonly data = input.required<ContentLayout2>();
  title = signal<Title>(null!);
  scheduleTitle = signal<Title>(null!);
  rdvParams = signal<{ store?: string; label?: string; isHomeAppointment?: boolean; phone?: string }>({});
  componentLoaded = signal(false);
  isMiddleOfficeToken$ = inject(MiddleOfficeService).isMiddleOfficeToken$();
  reviews = signal<AverageGrade>(null!);
  private readonly blockOne = inject(PageStore).selectPath((state) => state.block[0]);
  private readonly isBrowser = inject(ContextService).isBrowser();
  private readonly devicePixelRatio = inject(ResponsiveService).devicePixelRatio;
  private readonly storeNameService = inject(StoreNameService);
  private readonly seoService = inject(SeoService);
  private readonly destroy$ = new Subject<boolean>();
  private readonly storeName = signal<string>(null!);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly reviewsService = inject(ReviewsService);
  private readonly uuid = inject(PageStore).selectPath((state) => state.config.uuid);
  readonly #logsService = inject(LogsService);
  // null until Elasticsearch answers, so an already opened store never flashes its opening-soon image
  protected readonly storeScheduleDetails = toSignal(
    toObservable(this.uuid).pipe(
      filter(() => this.isBrowser),
      filter((uuid): uuid is string => !!uuid),
      switchMap((uuid) => this.elasticSearchService.getStoreScheduleDetails(uuid)),
      catchError((err: unknown) => {
        this.#logsService.error('ContentLayout2Component', 'Store schedule fetch failed', err);
        return of(undefined);
      })
    ),
    { initialValue: null }
  );
  protected readonly openingSoonImage = computed(() => {
    const image = this.data().imageOpeningSoon;
    const details = this.storeScheduleDetails();
    if (!image || details === null) return undefined;
    const openingDate = details?.openingDate;
    return openingDate && openingDate <= parisDate.format(new Date()) ? undefined : image;
  });

  ngOnInit(): void {
    const storeUuid = this.data().uuid;
    combineLatest([
      this.reviewsService.getAverageGrade(storeUuid).pipe(
        catchError((err: unknown) => {
          this.#logsService.error('ContentLayout2Component', 'Average grade fetch failed', err);
          return of<AverageGrade>({ totalStores: 0, averageGrade: 0, totalReviews: 0 });
        })
      ),
      this.storeNameService.getStoreName$(),
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(([reviews, storeLabel]) => {
          this.reviews.set(reviews);
          this.storeName.set(storeLabel || '');
          if (storeLabel && this.data().uuid) {
            this.rdvParams.set({
              store: this.data().uuid,
              label: storeLabel,
              isHomeAppointment: this.data().isHomeAppointment,
              phone: this.data().phone,
            });
          }

          const storeImage = this.getStoreImage();
          const storeJson = this.computeJsonLdSchema(reviews, storeImage);
          this.seoService.addJsonLdScript('store', storeJson);

          this.componentLoaded.set(true);
        })
      )
      .subscribe();
    if (this.data().title) {
      this.title.set({
        level: 'SPAN',
        type: 'NORMAL',
        color: 'BLACK',
        text: this.data().title!,
        align: {
          mobile: 'LEFT',
          desktop: 'LEFT',
        },
        size: {
          mobile: 'H2',
          desktop: 'H3',
        },
      });
    }
    if (this.data().scheduleTitle) {
      this.scheduleTitle.set({
        level: 'SPAN',
        type: 'SLASH',
        color: 'GREY_LIGHT',
        text: this.data().scheduleTitle!,
        weight: 500,
        align: {
          mobile: 'LEFT',
          desktop: 'LEFT',
        },
        size: {
          mobile: 'SPAN',
          desktop: 'SPAN',
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private getStoreImage(): string {
    const block = this.blockOne();

    // Ensure the block is a hero block with the expected layout
    if (
      block?.type === 'hero' &&
      typeof block.data === 'object' &&
      (
        block.data as {
          layout: string;
        }
      ).layout === 'hero_layout2'
    ) {
      // Safely cast to the expected HeroLayout2 type
      const heroData = block.data as { data: HeroLayout2 };
      const { images } = heroData.data;

      // Return the first image link, or empty string if missing
      const image = images?.[0]?.link ?? '';
      if (image === '') {
        return '';
      }
      return getCDNLink(images[0], { width: 1200, height: 675 }, this.devicePixelRatio);
    }

    return '';
  }

  private computeJsonLdSchema(reviews: AverageGrade, storeImage: string): JsonLD {
    return {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: this.storeName(),
      image: storeImage,
      address: this.data().address || '',
      telephone: this.data().phone || '',
      ...(reviews.totalReviews >= 1
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: reviews.averageGrade,
              ratingCount: reviews.totalReviews,
            },
          }
        : {}),
      priceRange: '€€€',
      currenciesAccepted: 'EUR',
      brand: this.storeName().split(' ')[0],
    };
  }
}
